// 發票管理: 發送追蹤 / 收款 / 調整金額 / PDF
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, auth, money, fmtDate, todayStr, INV_STATUS, r2 } from '../api.js';
import { Modal, Field, StatusBadge, Loading, Empty, PageHead, useToast, useConfirm } from '../ui.jsx';
import { InvoiceDoc } from '../docs.jsx';
import { DocPreview } from '../docs.jsx';

function PayModal({ inv, onClose, done }) {
  const { toast } = useToast();
  const remain = r2(inv.adjusted_amount - inv.paid_amount);
  const [f, setF] = useState({ pay_date: todayStr(), amount: remain, method: 'bank', reference: '' });
  const pay = async () => {
    try {
      await api.post(`/invoices/${inv.id}/payments`, f);
      toast(`已收款 HK$ ${money(f.amount)} ✔`); done();
    } catch (e) { toast(e.message, 'err'); }
  };
  const clearAll = async () => {
    try { const r = await api.post(`/invoices/${inv.id}/clear`, { pay_date: f.pay_date, method: f.method }); toast(`已全數結清 HK$ ${money(r.amount)} ✔`); done(); }
    catch (e) { toast(e.message, 'err'); }
  };
  return (
    <Modal onClose={onClose} icon="💵" title={`收款 — ${inv.invoice_no}`}
      footer={<><button className="btn" onClick={onClose}>取消</button>
        <button className="btn mint" onClick={clearAll}>✅ 一鍵結清 (HK$ {money(remain)})</button>
        <button className="btn primary" onClick={pay}>💾 記錄收款</button></>}>
      <div className="total-bar mb">
        <div className="line"><span>應收</span><span className="v">HK$ {money(inv.adjusted_amount)}</span></div>
        <div className="line"><span>已收</span><span className="v">HK$ {money(inv.paid_amount)}</span></div>
        <div className="line grand"><span>未收餘數</span><span className="v">HK$ {money(remain)}</span></div>
      </div>
      <div className="form-grid">
        <Field label="收款日期"><input className="input" type="date" value={f.pay_date} onChange={e => setF({ ...f, pay_date: e.target.value })} /></Field>
        <Field label="金額"><input className="input num-input" type="number" step="0.01" value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} /></Field>
        <Field label="方式">
          <select className="select" value={f.method} onChange={e => setF({ ...f, method: e.target.value })}>
            <option value="bank">銀行轉帳</option><option value="cheque">支票</option><option value="cash">現金</option><option value="other">其他</option>
          </select>
        </Field>
        <Field label="參考"><input className="input" value={f.reference} onChange={e => setF({ ...f, reference: e.target.value })} placeholder="支票號碼等" /></Field>
      </div>
    </Modal>
  );
}

function AdjustModal({ inv, onClose, done }) {
  const { toast } = useToast();
  const [f, setF] = useState({ adjusted_amount: inv.adjusted_amount, issue_date: inv.issue_date, due_date: inv.due_date, notes: inv.notes || '' });
  const save = async () => {
    try { await api.put(`/invoices/${inv.id}`, f); toast('發票已調整 ✔'); done(); } catch (e) { toast(e.message, 'err'); }
  };
  return (
    <Modal onClose={onClose} icon="✏️" title={`調整發票 — ${inv.invoice_no}`}
      footer={<><button className="btn" onClick={onClose}>取消</button><button className="btn primary" onClick={save}>💾 儲存</button></>}>
      <p className="muted mb">適用於價錢爭議或扣減 — 原金額 HK$ {money(inv.amount)} 會保留紀錄，應收改為以下金額。</p>
      <div className="form-grid">
        <Field label="最終應收金額"><input className="input num-input" type="number" step="0.01" value={f.adjusted_amount} onChange={e => setF({ ...f, adjusted_amount: e.target.value })} /></Field>
        <Field label="開出日期"><input className="input" type="date" value={f.issue_date} onChange={e => setF({ ...f, issue_date: e.target.value })} /></Field>
        <Field label="到期日"><input className="input" type="date" value={f.due_date} onChange={e => setF({ ...f, due_date: e.target.value })} /></Field>
        <Field label="備註" span2><input className="input" value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} /></Field>
      </div>
    </Modal>
  );
}

function DetailModal({ inv, onClose, onRefresh }) {
  const { toast } = useToast();
  const [d, setD] = useState(null);
  const [st, setSt] = useState({});
  const [preview, setPreview] = useState(false);
  const load = () => api.get(`/invoices/${inv.id}`).then(setD);
  useEffect(() => { load(); api.get('/settings').then(setSt); }, [inv.id]);
  if (!d) return null;
  const delPay = async (p) => { try { await api.del(`/payments/${p.id}`); toast('收款已刪除'); load(); onRefresh(); } catch (e) { toast(e.message, 'err'); } };
  const toPDF = async () => {
    try {
      toast('正在產生 PDF…');
      const resp = await fetch('/api/invoices/' + d.id + '/generate-pdf', { headers: { Authorization: 'Bearer ' + auth.token } });
      if (!resp.ok) throw new Error('PDF 生成失敗 (' + resp.status + ')');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = d.invoice_no + '.pdf'; a.click();
      URL.revokeObjectURL(url);
      toast('PDF 已下載 ✔');
    } catch (e) { toast(e.message, 'err'); }
  };
  return (
    <Modal size="lg" onClose={onClose} icon="🧾" title={`${d.invoice_no} — ${d.customer_name}`}
      footer={<>
        <button className="btn" onClick={onClose}>關閉</button>
        <button className="btn" onClick={() => setPreview(true)}>👁 預覽</button>
        <button className="btn" onClick={toPDF}>📥 PDF 下載</button>
      </>}>
      <div className="kv mb">
        <div>工程訂單</div><div>{d.order_no || '獨立發票'}</div>
        <div>分期</div><div>{d.installment_label}{d.installment_count > 1 ? ` (${d.installment_no}/${d.installment_count})` : ''}</div>
        <div>開出 / 到期</div><div>{fmtDate(d.issue_date)} → {fmtDate(d.due_date)}</div>
        <div>金額 / 應收</div><div>HK$ {money(d.amount)} → HK$ {money(d.adjusted_amount)}</div>
        <div>狀態</div><div><StatusBadge map={INV_STATUS} value={d.status} /></div>
      </div>
      <div className="card-title" style={{ marginBottom: 8 }}>收款紀錄</div>
      {d.payments.length === 0 ? <div className="muted mb">未有收款</div> : (
        <div className="tbl-wrap mb">
          <table className="tbl">
            <thead><tr><th>日期</th><th>方式</th><th>參考</th><th className="num">金額</th><th></th></tr></thead>
            <tbody>
              {d.payments.map(p => (
                <tr key={p.id}>
                  <td>{fmtDate(p.pay_date)}</td><td>{p.method || '—'}</td><td>{p.reference || '—'}</td>
                  <td className="num strong">{money(p.amount)}</td>
                  <td><button className="btn sm coral" onClick={() => delPay(p)}>✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {d.order_items?.length ? (
        <>
          <div className="card-title" style={{ marginBottom: 8 }}>相關工程項目</div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead><tr><th>描述</th><th className="num">數量</th><th className="num">金額</th></tr></thead>
              <tbody>
                {d.order_items.map((it, i) => <tr key={i}><td>{it.description}</td><td className="num">{it.qty} {it.unit}</td><td className="num">{money(it.amount)}</td></tr>)}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
      {preview && (
        <DocPreview title={`預覽 — ${d.invoice_no}`} onClose={() => setPreview(false)}>
          <InvoiceDoc data={d} settings={st} />
        </DocPreview>
      )}
    </Modal>
  );
}

export default function Invoices() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [confirm, confirmNode] = useConfirm();
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [pay, setPay] = useState(null);
  const [adjust, setAdjust] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reminders, setReminders] = useState([]);
  useEffect(() => { api.get('/reminders/invoices').then(r => setReminders(r.reminders || [])).catch(() => {}); }, []);
  const refresh = () => api.get(`/invoices?${new URLSearchParams({ ...(status ? { status } : {}), ...(q ? { q } : {}) })}`).then(setRows);
  useEffect(() => { refresh(); }, [status, q]);

  const submit = async (inv) => { try { await api.post(`/invoices/${inv.id}/submit`); toast(`${inv.invoice_no} 已標記為已發送 📤`); refresh(); } catch (e) { toast(e.message, 'err'); } };
  const voidInv = (inv) => confirm(`作廢發票 ${inv.invoice_no}？`, async () => {
    try { await api.post(`/invoices/${inv.id}/void`); toast('已作廢'); refresh(); } catch (e) { toast(e.message, 'err'); }
  }, { danger: true, okLabel: '作廢' });
  const delInv = (inv) => confirm(`永久刪除發票 ${inv.invoice_no}？\n收款紀錄會一齊刪除，動作不可還原。`, async () => {
    try { await api.del(`/invoices/${inv.id}`); toast(`${inv.invoice_no} 已刪除`); refresh(); } catch (e) { toast(e.message, 'err'); }
  }, { danger: true, okLabel: '永久刪除' });
  const toPDFQuick = async (inv) => {
    try {
      toast('正在產生 PDF…');
      const resp = await fetch('/api/invoices/' + inv.id + '/generate-pdf', { headers: { Authorization: 'Bearer ' + auth.token } });
      if (!resp.ok) throw new Error('PDF 生成失敗');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = inv.invoice_no + '.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast(e.message, 'err'); }
  };
  const today = todayStr();
  const shareMsg = (i2) => '報價單/發票 ' + i2.invoice_no + ' — 客戶: ' + i2.customer_name + ' — 應收 HK$ ' + money(i2.adjusted_amount - i2.paid_amount) + ' — 到期日 ' + i2.due_date;
  const sendDoc = async (i2, channel) => {
    try {
      toast('正在生成 PDF 同準備發送…');
      const r = await api.post('/invoices/' + i2.id + '/send', { channel });
      if (r.url) { window.open(r.url, '_blank'); toast((channel === 'whatsapp' ? 'WhatsApp' : 'Email') + ' 已準備 — PDF 連結已附上 ✔'); }
      else toast('已經 ' + (r.channel || '') + ' 發送 ✔');
      refresh();
    } catch (e) { toast(e.message, 'err'); }
  };
  const shareWA = (i2) => sendDoc(i2, 'whatsapp');
  const shareMail = (i2) => sendDoc(i2, 'email');
  const totals = (rows || []).reduce((a, i) => {
    if (i.status !== 'void') { a.sum += i.adjusted_amount; a.paid += i.paid_amount; }
    return a;
  }, { sum: 0, paid: 0 });

  return (
    <div>
      <PageHead icon="🧾" title="發票 Invoices" desc={`合計 HK$ ${money(totals.sum)} · 已收 HK$ ${money(totals.paid)} · 未收 HK$ ${money(r2(totals.sum - totals.paid))}`}
        actions={<>
          <div className="search-box"><input className="input" placeholder="搜尋發票/客戶…" value={q} onChange={e => setQ(e.target.value)} /></div>
        </>} />
      {(reminders || []).filter(x => x.level !== 'info').length > 0 ? (
        <div className="card" style={{ borderColor: '#E5E7EB', borderLeft: '4px solid #111827', padding: '12px 16px', marginBottom: 16 }}>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <strong>⏰ 收款提醒 — {reminders.filter(x => x.level !== 'info').length} 張需要跟進</strong>
          </div>
          {reminders.filter(x => x.level !== 'info').slice(0, 5).map(x => (
            <div key={x.id} className="row" style={{ justifyContent: 'space-between', fontSize: 13, padding: '6px 0', borderBottom: '1px dashed var(--line)' }}>
              <span><strong>{x.invoice_no}</strong> {x.customer_name} — 應收 HK$ {money(x.remain)} — <span style={{ color: x.level === 'urgent' ? 'var(--ink)' : 'var(--ink-2)', fontWeight: 800 }}>{x.type === 'overdue_30d' ? '⚠️ 過期 30 日+' : x.type === 'overdue_7d' ? '過期 ' + x.days_overdue + ' 日' : x.type === 'overdue' ? '今日到期' : ''}</span></span>
              <span className="muted">{x.customer_phone || x.customer_email || ''}</span>
            </div>
          ))}
        </div>
      ) : null}
      <div className="row mb">
        <div className="pill-filter">
          <button className={`pill ${!status ? 'active' : ''}`} onClick={() => setStatus('')}>全部</button>
          <button className={`pill ${status === 'open' ? 'active' : ''}`} onClick={() => setStatus('open')}>⚠️ 未找數</button>
          {Object.entries(INV_STATUS).map(([k, v]) => (
            <button key={k} className={`pill ${status === k ? 'active' : ''}`} onClick={() => setStatus(k)}>{v.label}</button>
          ))}
        </div>
      </div>
      <div className="card">
        {rows === null ? <Loading /> : rows.length === 0 ? <Empty icon="🧾" text="沒有發票 — 到訂單頁按「產生發票」" /> : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead><tr><th>發票</th><th>客戶</th><th>訂單 / 主題</th><th className="num">訂單總價</th><th>開出</th><th>到期</th><th className="num">應收</th><th className="num">已收</th><th>狀態</th><th style={{ minWidth: 250 }}></th></tr></thead>
              <tbody>
                {rows.map(i => {
                  const overdue = i.status !== 'void' && i.status !== 'paid' && i.due_date && i.due_date < today;
                  return (
                    <tr key={i.id} className="clickable" onClick={() => setDetail(i)}>
                      <td className="strong">{i.invoice_no}{i.installment_count > 1 ? <span className="sub"> ({i.installment_no}/{i.installment_count})</span> : null}</td>
                      <td>{i.customer_name}</td>
                      <td style={{ maxWidth: 240 }}>{i.order_no ? <><span className="strong">{i.order_no}</span>{i.order_title ? <span className="sub" style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.order_title}</span> : null}</> : '—'}</td>
                      <td className="num">{i.order_total != null ? money(i.order_total) : '—'}</td>
                      <td>{fmtDate(i.issue_date)}</td>
                      <td style={{ color: overdue ? 'var(--coral)' : undefined, fontWeight: overdue ? 700 : undefined }}>{overdue ? '⚠️ ' : ''}{fmtDate(i.due_date)}</td>
                      <td className="num strong">{money(i.adjusted_amount)}</td>
                      <td className="num">{money(i.paid_amount)}</td>
                      <td><StatusBadge map={INV_STATUS} value={i.status} />{overdue ? <span className="st st-red" style={{ marginLeft: 4 }}>逾期</span> : null}</td>
                      <td onClick={e => e.stopPropagation()}>
                        <div className="row" style={{ flexWrap: 'nowrap' }}>
                          {i.status === 'pending' && <button className="btn sm amber" onClick={() => submit(i)}>📤 已發送</button>}
                          {i.status !== 'paid' && i.status !== 'void' && <button className="btn sm mint" onClick={() => setPay(i)}>💵 收款</button>}
                          {i.status !== 'void' && <button className="btn sm" onClick={() => setAdjust(i)}>✏️ 調整</button>}
                          <button className="btn sm" onClick={() => toPDFQuick(i)}>📥</button>
                          <button className="btn sm" title="WhatsApp 俾客戶" onClick={() => shareWA(i)}>📱</button>
                          <button className="btn sm" title="Email 俾客戶" onClick={() => shareMail(i)}>✉️</button>
                          {i.status !== 'void' && i.status !== 'paid' && <button className="btn sm coral" title="作廢" onClick={() => voidInv(i)}>🚫</button>}
                          {api.user?.role === 'admin' && <button className="btn sm coral" title="永久刪除 (管理員)" onClick={() => delInv(i)}>🗑️</button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {pay && <PayModal inv={pay} onClose={() => setPay(null)} done={() => { setPay(null); refresh(); }} />}
      {adjust && <AdjustModal inv={adjust} onClose={() => setAdjust(null)} done={() => { setAdjust(null); refresh(); }} />}
      {detail && <DetailModal inv={detail} onClose={() => { setDetail(null); refresh(); }} onRefresh={refresh} />}
      {confirmNode}
    </div>
  );
}
